import React, { useEffect, useState, useCallback } from 'react';
import { authAPI, projectAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import { handleError } from '../utils/errorHandler';
import ErrorDisplay from '../components/ErrorDisplay';
import '../styles/adminCommonStyles.css';
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY; // Secure the API key

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Add Project Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    description: '',
    region: '',
    start_date: '',
    end_date: ''
  });
  const [addImage, setAddImage] = useState(null);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Edit Project Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    description: '',
    region: '',
    start_date: '',
    end_date: ''
  });
  const [editImage, setEditImage] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  
  // Delete States
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [removeEditPhoto, setRemoveEditPhoto] = useState(false);
  
  // Success Messages
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();

  const checkUserRole = () => {
    try {
      const hasSuperAdminRole = authAPI.hasRole('superadmin');
      setIsSuperAdmin(hasSuperAdminRole);
      console.log('User is superadmin:', hasSuperAdminRole);
    } catch (err) {
      console.error('Error checking user role:', err);
      setIsSuperAdmin(false);
    }
  };

  // Fetch projects with authentication
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await projectAPI.getProjects();
      const { normalizeList } = await import('../utils/responseHelpers');
      const data = normalizeList(response);
      const withImages = await preloadProjectImages(data);
      setProjects(withImages);
    } catch (err) {
      console.error('Error fetching projects:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        authAPI.logout();
        navigate('/admin/login');
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch projects';
        setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to fetch projects');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const preloadProjectImages = async (projectsList) => {
    // Don't preload as blobs - just return as-is
    // Cache busting via query params in resolveProjectImageUrl handles freshness
    return projectsList;
  };

  useEffect(() => {
    checkUserRole();
    fetchProjects();
  }, [fetchProjects]);

  const resolveProjectImageUrl = (project) => {
    if (!project || !project.image_url) return null;
    const u = project.image_url;
    if (u.startsWith('blob:') || u.startsWith('data:')) return u;
    // Always add cache busting for fresh images
    const cacheParam = `?t=${Date.now()}`;
    if (u.startsWith('/api/')) return API_BASE + u + cacheParam;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/images/')) return API_BASE + u + cacheParam;
    if (!isNaN(Number(u))) {
      // legacy uploads table id
      return `${API_BASE}/image/${u}${cacheParam}`;
    }
    return null;
  };

  // Show success message helper
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle add form input
  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  const handleAddImage = (e) => {
    setAddImage(e.target.files[0]);
  };

  // Handle add form submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
      if (addImage && addImage.size > 100 * 1024 * 1024) {
        setAddError('Image is too big. Please upload an image less than 100 MB.');
        setAddLoading(false);
        return;
      }
      // send multipart FormData directly to /api/projects so backend can store image
      const formData = new FormData();
      formData.append('name', addForm.name);
      formData.append('description', addForm.description);
      formData.append('region', addForm.region);
      formData.append('start_date', addForm.start_date);
      formData.append('end_date', addForm.end_date);
      if (addImage) formData.append('image', addImage);
      await projectAPI.createProject(formData, true);
      setShowAddModal(false);
      setAddForm({
        name: '',
        description: '',
        region: '',
        start_date: '',
        end_date: ''
      });
      setAddImage(null);
      showSuccess('Project added successfully!');
      await fetchProjects();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add project';
      setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add project');
    } finally {
      setAddLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (project) => {
    setEditForm({
      id: project.id,
      name: project.name,
      description: project.description,
      region: project.region,
      start_date: project.start_date ? project.start_date.slice(0, 10) : '',
      end_date: project.end_date ? project.end_date.slice(0, 10) : '',
      image_url: project.image_url || ''
    });
    setEditImage(null);
    setRemoveEditPhoto(false);
    setShowEditModal(true);
    setEditError(null);
    // Scroll to edit form
    scrollToForm();
  };

  const handleEditImage = (e) => {
    setEditImage(e.target.files[0]);
  };

  // Handle edit form input
  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  // Handle edit form submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      if (editImage && editImage.size > 100 * 1024 * 1024) {
        setEditError('Image is too big. Please upload an image less than 100 MB.');
        setEditLoading(false);
        return;
      }
      
      if (editImage) {
        // use FormData for updates when an image is included
        const formData = new FormData();
        formData.append('name', editForm.name);
        formData.append('description', editForm.description);
        formData.append('region', editForm.region);
        formData.append('start_date', editForm.start_date);
        formData.append('end_date', editForm.end_date);
        formData.append('image', editImage);
        await projectAPI.updateProject(editForm.id, formData, true);
      } else if (removeEditPhoto) {
        // Send JSON with remove_image flag
        const data = {
          name: editForm.name,
          description: editForm.description,
          region: editForm.region,
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          remove_image: 'true'
        };
        await projectAPI.updateProject(editForm.id, data, false);
      } else {
        // Regular update without image changes
        const data = {
          name: editForm.name,
          description: editForm.description,
          region: editForm.region,
          start_date: editForm.start_date,
          end_date: editForm.end_date
        };
        await projectAPI.updateProject(editForm.id, data, false);
      }
      setShowEditModal(false);
      setEditImage(null);
      setRemoveEditPhoto(false);
      setEditForm({
        id: '',
        name: '',
        description: '',
        region: '',
        start_date: '',
        end_date: ''
      });
      showSuccess('Project updated successfully!');
      await fetchProjects();
    } catch (err) {
        // If the upload failed due to server-side multipart limits (empty $_FILES / 400),
        // try a safe fallback: upload the image to the generic `/upload` endpoint and
        // then update the project with the returned URL.
        const status = err.response?.status;
        if (editImage && status === 400) {
          try {
            const fd = new FormData();
            fd.append('image', editImage);
            const up = await authAPI.uploadImage(fd);
            const returnedUrl = up?.data?.url || up?.data?.path || up?.data?.file || up?.data;
            if (returnedUrl) {
              // Update project record with image URL
              await projectAPI.updateProject(editForm.id, { image_url: returnedUrl });
              setShowEditModal(false);
              setEditImage(null);
              setEditForm({ id: '', name: '', description: '', region: '', start_date: '', end_date: '' });
              showSuccess('Project updated successfully!');
              await fetchProjects();
              return;
            }
          } catch (upErr) {
            console.error('Fallback upload failed:', upErr);
          }
        }

        const errorMsg = err.response?.data?.message || err.message || 'Failed to update project';
        setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update project');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle delete - FIXED: Proper implementation with superadmin check
  const handleDeleteProject = async (projectId, projectName) => {
    // Check if user is superadmin before allowing delete
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete projects.');
      return;
    }

    // Double confirmation for delete
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${projectName}"? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    setDeleteLoading(projectId);
    setError(null);

    try {
      console.log('Attempting to delete project:', projectId);
      await projectAPI.deleteProject(projectId);
      
      // Remove project from local state immediately for better UX
      setProjects(prevProjects => prevProjects.filter(project => project.id !== projectId));
      
      showSuccess('Project deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      
      if (error.response?.status === 403) {
        setError('Access denied. Superadmin role required to delete projects.');
      } else if (error.response?.status === 404) {
        setError('Project not found.');
      } else if (error.response?.status === 401) {
        setError('Please log in again.');
        authAPI.logout();
        navigate('/admin/login');
      } else {
        setError(error.message || 'Failed to delete project');
      }
    } finally {
      setDeleteLoading(null);
    }
  };

  // Logout function
  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  return (
  <div className="w-full max-w-6xl mx-auto px-1 md:px-2 py-2 landscape:px-4 landscape:py-4 flex flex-col items-center justify-center bg-transparent">
    <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6">
      <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left">Manage Projects</h2>
      <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
          {isSuperAdmin ? 'Super Admin' : 'Admin'}
        </span>
        <button
          onClick={handleLogout}
          className="admin-btn-danger"
        >
          Logout
        </button>
      </div>
    </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="admin-alert-error">
          <strong>Error:</strong> <ErrorDisplay error={error} className="text-red-700 font-medium" />
        </div>
      )}

      <button
        className="admin-btn-primary-lg mb-6"
        onClick={() => {
            setShowAddModal(true);
            scrollToForm();
          }}
      >
        + Add New Project
      </button>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-modal-form" id="add-project-form">
            <h3 className="text-xl font-bold mb-4">Add New Project</h3>
            <form onSubmit={handleAddSubmit}>
              {addError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {addError}
                </div>
              )}
              
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Editor
                  value={addForm.description}
                  onEditorChange={val => setAddForm(f => ({ ...f, description: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 420,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">Region</label>
                <input
                  type="text"
                  name="region"
                  value={addForm.region}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={addForm.start_date}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">End Date (Optional)</label>
                <input
                  type="date"
                  name="end_date"
                  value={addForm.end_date}
                  onChange={handleAddInput}
                  className="admin-input"
                />
              </div>
              
              <div className="mb-6">
                <label className="admin-label">Image (Optional)</label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleAddImage}
                  className="admin-input"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="admin-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLoading}
                  className="admin-btn-primary"
                >
                  {addLoading ? 'Adding...' : 'Add Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="admin-modal-overlay-scroll">
          <div className="admin-modal-content admin-modal-form" id="edit-project-form">
            <h3 className="text-xl font-bold mb-4">Edit Project</h3>
            <form onSubmit={handleEditSubmit}>
              {editError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {editError}
                </div>
              )}
              
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Editor
                  value={editForm.description}
                  onEditorChange={val => setEditForm(f => ({ ...f, description: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 420,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">Region</label>
                <input
                  type="text"
                  name="region"
                  value={editForm.region}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={editForm.start_date}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="admin-label">End Date (Optional)</label>
                <input
                  type="date"
                  name="end_date"
                  value={editForm.end_date}
                  onChange={handleEditInput}
                  className="admin-input"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Image (Optional)</label>
                {editForm.image_url && !removeEditPhoto && (
                  <div className="mb-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 mb-2">Current Image:</p>
                    <img
                      src={resolveProjectImageUrl(editForm)}
                      alt="Current"
                      className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      className="mt-3 admin-btn-danger"
                      onClick={() => setRemoveEditPhoto(true)}
                      disabled={editLoading}
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                {removeEditPhoto && (
                  <div className="mb-3 p-4 bg-red-50 border border-red-300 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mb-2">Image will be removed</p>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setRemoveEditPhoto(false)}
                      disabled={editLoading}
                    >
                      Keep Image
                    </button>
                  </div>
                )}
                {!removeEditPhoto && (
                  <>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleEditImage}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {editImage && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                        <p className="text-sm font-medium text-[#3a8b9e] mb-2">New Image Preview:</p>
                        <img
                          src={URL.createObjectURL(editImage)}
                          alt="New"
                          className="h-32 w-32 object-cover rounded-lg border border-blue-300"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="admin-btn-primary"
                >
                  {editLoading ? 'Updating...' : 'Update Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading projects..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
  <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Name</th>
                <th className="admin-table-header-cell">Description</th>
                <th className="admin-table-header-cell">Start Date</th>
                <th className="admin-table-header-cell">End Date</th>
                <th className="admin-table-header-cell">Image</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {projects.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No projects found</div>
                    <div className="text-sm mt-1">Add your first project to get started!</div>
                  </td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="hover:bg-blue-50 transition-colors">
                    <td className="py-2 px-2 font-medium text-gray-900">
                      {project.name && project.name.length > 50 ? project.name.slice(0, 50) + '…' : project.name}
                    </td>
                    <td className="py-2 px-2 max-w-xs break-words" title={project.description}>
                      <SafeRichText content={project.description && project.description.length > 50 ? project.description.slice(0, 50) + '…' : project.description} />
                    </td>
                    <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{project.start_date ? project.start_date.split('T')[0] : ''}</td>
                    <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{project.end_date ? project.end_date.split('T')[0] : 'Ongoing'}</td>
                    <td className="py-2 px-2">
                      {resolveProjectImageUrl(project) ? (
                        <img 
                          src={resolveProjectImageUrl(project)}
                          alt="Project" 
                          className="h-12 w-12 object-cover rounded"
                        />
                      ) : (
                        <span className="text-gray-400 text-sm">No image</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-1 items-stretch justify-center">
                        <button
                          onClick={() => handleEditClick(project)}
                          className="admin-btn-table-edit"
                        >
                          Edit
                        </button>
                        {isSuperAdmin ? (
                          <button
                            onClick={() => handleDeleteProject(project.id, project.name)}
                            disabled={deleteLoading === project.id}
                            className="admin-btn-table-danger"
                          >
                            {deleteLoading === project.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="px-2 py-1 bg-gray-400 text-white rounded-lg cursor-not-allowed text-xs"
                            title="Superadmin role required to delete projects"
                          >
                          
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}